import { Col, Row, Typography } from 'antd'
import React, { FunctionComponent, useMemo } from 'react'

import {
  AnnuityResult,
  BalloonResult,
  calculateAnnuity,
  calculateBalloon,
  generateAnnuitySchedule,
  LoanInput,
  LoanType,
} from '../../utils/loanCalculatorHelpers'
import { IncreasingNumber } from '../IncreasingNumber/IncreasingNumber'
import { LoanPaymentChart } from '../LoanPaymentChart/LoanPaymentChart'

const { Title } = Typography

interface LoanCalculatorOutputProps {
  loanType: LoanType
  loanInput: LoanInput
}

export const LoanCalculatorOutput: FunctionComponent<LoanCalculatorOutputProps> =
  ({ loanType, loanInput }) => {
    const results = useMemo<AnnuityResult | BalloonResult | null>(() => {
      const { amount, months, interestRate } = loanInput

      if (loanType === LoanType.Annuity) {
        return calculateAnnuity(amount, months, interestRate)
      } else {
        return calculateBalloon(amount, months, interestRate)
      }
    }, [loanType, loanInput])

    const scheduleData = useMemo(() => {
      const { amount, months, interestRate } = loanInput

      if (loanType === LoanType.Annuity) {
        return generateAnnuitySchedule(amount, months, interestRate)
      }
      return []
    }, [loanType, loanInput])

    const colStyle = useMemo(() => {
      if (loanType === LoanType.Balloon) return { xs: 24, sm: 12, md: 6 }
      return { xs: 24, sm: 12, md: 8 }
    }, [loanType])

    if (!results) return null

    return (
      <div className="mt-8">
        <Title level={4} className="mb-4">
          Výsledky
        </Title>

        <Row gutter={[16, 24]}>
          <>
            {results?.monthlyPayment && (
              <Col {...colStyle}>
                <div className="mb-2">Měsíční splátka</div>
                <IncreasingNumber
                  className="text-2xl text-lime-600"
                  finalNumber={results.monthlyPayment}
                  decimal={0}
                  suffix=" Kč"
                />
              </Col>
            )}

            {results.totalPayment && (
              <Col {...colStyle}>
                <div className="mb-2">Celkem ke splacení</div>
                <IncreasingNumber
                  className="text-2xl"
                  finalNumber={results.totalPayment}
                  decimal={0}
                  suffix=" Kč"
                />
              </Col>
            )}

            {results.totalInterest && (
              <Col {...colStyle}>
                <div className="mb-2">Celkové úroky</div>
                <IncreasingNumber
                  className="text-2xl"
                  finalNumber={results.totalInterest}
                  decimal={0}
                  suffix=" Kč"
                />
              </Col>
            )}

            {'finalPayment' in results && results.finalPayment !== undefined && (
              <Col {...colStyle}>
                <div className="mb-2">Finální splátka</div>
                <IncreasingNumber
                  className="text-2xl"
                  finalNumber={results.finalPayment as number}
                  decimal={0}
                  suffix=" Kč"
                />
              </Col>
            )}
          </>
        </Row>

        {loanType === LoanType.Annuity && (
          <LoanPaymentChart data={scheduleData} />
        )}
      </div>
    )
  }
